import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, Clock, AlertCircle, Package, MessageCircle, DollarSign, Users } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { toast } from 'sonner';

const PendingTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState({
    pendingOrders: [],
    pendingCommissions: [],
    unreadMessages: [],
    newSignups: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
      return;
    }

    loadPendingTasks();
  }, [navigate]);

  const loadPendingTasks = async () => {
    try {
      // Load all pending items
      const [orders, transactions, messages, users] = await Promise.all([
        adminAPI.getAllOrders(),
        adminAPI.getAllTransactions(),
        adminAPI.getAllMessages(),
        adminAPI.getAllUsers()
      ]);

      setTasks({
        pendingOrders: orders.filter(o => o.status === 'Pending'),
        pendingCommissions: transactions.filter(t => t.status === 'Pending'),
        unreadMessages: messages.filter(m => m.status === 'Unread'),
        newSignups: users.slice(0, 5) // Last 5 signups
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading pending tasks:', error);
      toast.error('Failed to load pending tasks');
      setIsLoading(false);
    }
  };

  const totalPending = tasks.pendingOrders.length + tasks.pendingCommissions.length + tasks.unreadMessages.length;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Pending Tasks</h1>
          <p className="text-gray-700 text-lg font-medium">Manage all pending items and approvals</p>
          <div className="mt-3">
            <Badge className="bg-orange-100 text-orange-800 text-lg px-4 py-2">
              <AlertCircle className="h-5 w-5 mr-2" />
              {totalPending} Total Pending Items
            </Badge>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card 
            className="border-3 border-yellow-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer hover:border-yellow-500 transition-all"
            onClick={() => navigate('/admin/orders')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-700">
                <Package className="h-5 w-5" />
                Pending Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-yellow-600">{tasks.pendingOrders.length}</p>
              <p className="text-sm text-gray-600 mt-2">Need review</p>
            </CardContent>
          </Card>

          <Card 
            className="border-3 border-blue-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer hover:border-blue-500 transition-all"
            onClick={() => navigate('/admin/commissions')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <DollarSign className="h-5 w-5" />
                Pending Commissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600">{tasks.pendingCommissions.length}</p>
              <p className="text-sm text-gray-600 mt-2">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card 
            className="border-3 border-orange-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer hover:border-orange-500 transition-all"
            onClick={() => navigate('/admin/messages')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <MessageCircle className="h-5 w-5" />
                Unread Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-600">{tasks.unreadMessages.length}</p>
              <p className="text-sm text-gray-600 mt-2">Need response</p>
            </CardContent>
          </Card>

          <Card 
            className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer hover:border-green-500 transition-all"
            onClick={() => navigate('/admin/users')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Users className="h-5 w-5" />
                Recent Signups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{tasks.newSignups.length}</p>
              <p className="text-sm text-gray-600 mt-2">New users</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Lists */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Pending Orders */}
          <Card className="border-4 border-yellow-400 shadow-2xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Pending Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {tasks.pendingOrders.length > 0 ? (
                  tasks.pendingOrders.map((order) => (
                    <div key={order._id || order.id} className="p-3 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{order.customerName}</p>
                          <p className="text-sm text-gray-600">{order.productType}</p>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleString('en-IN')}</p>
                      <Button 
                        size="sm" 
                        onClick={() => navigate('/admin/orders')}
                        className="mt-2 w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                      >
                        Review Order
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">No pending orders</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Unread Messages */}
          <Card className="border-4 border-orange-400 shadow-2xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Unread Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {tasks.unreadMessages.length > 0 ? (
                  tasks.unreadMessages.map((message) => (
                    <div key={message._id || message.id} className="p-3 bg-orange-50 rounded-lg border-2 border-orange-200">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{message.userName}</p>
                          <p className="text-sm text-gray-600">{message.subject}</p>
                        </div>
                        <Badge className="bg-orange-100 text-orange-800">New</Badge>
                      </div>
                      <p className="text-xs text-gray-700 mt-2">{message.message.substring(0, 60)}...</p>
                      <Button 
                        size="sm" 
                        onClick={() => navigate('/admin/messages')}
                        className="mt-2 w-full bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        Reply
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">No unread messages</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PendingTasks;
